import {
    productOptions_productOptions_items_ConfigurableProduct,
    productOptions_productOptions_items_ConfigurableProduct_configurable_options,
    productOptions_productOptions_items_ConfigurableProduct_configurable_options_values,
    productOptions_productOptions_items_ConfigurableProduct_variants,
    productOptions_productOptions_items_ConfigurableProduct_variants_attributes,
} from 'src/queries/__generated__/productOptions';
import { ProductConfigOptions, ProductConfigOptionsValues } from 'src/components/ProductDetail/ProductOptions';
import {
    BaseVariant,
    cleanMediaEntries,
    ConfigurableAttributeOption,
    ProductVariant,
} from 'src/components/ProductDetail/utils/product-detail.types';
import { productDetail_productDetail_media_gallery } from 'src/queries/__generated__/productDetail';
import { imagePath } from 'src/util/formatUrl';
import { StockStatus } from 'src/types/global-types';
import { getSwatchData } from 'src/components/ProductDetail/utils/swatchData';
import { productList_productList } from 'src/queries/__generated__/productList';
import { get } from 'es-toolkit/compat';

const EMPTY_ARR = [];

/**
 * Function for filtering colors without any quantity in sizes.
 */
const findVariantsWithNoQtyWithinAllSizes = (
    variants: (productOptions_productOptions_items_ConfigurableProduct_variants | null)[] | null,
) => {
    // es-toolkit's `get` returns `unknown | undefined`, whereas lodash narrowed
    // the type for known paths. Coerce explicitly to keep the downstream shape
    // (`{ label: string | null; max_qty: number | null }`) intact.
    const allLabelAndQuantity =
        variants &&
        variants.map((el) => {
            return {
                label: (get(el, 'attributes[0].label') ?? null) as string | null,
                max_qty: (get(el, 'product.stock_qty') ?? null) as number | null,
            };
        });

    // Group by Label and add quntities to one array. {Black: [1,4,2,13,21], Red: [12,31,12,2,4], ...}
    const groupByLabel = (labelAndQuantity: { label: string | null; max_qty: number | null }[] | null, key: string) => {
        return labelAndQuantity?.reduce((rv, x) => {
            (rv[x[key]] = rv[x[key]] || []).push(x.max_qty);
            return rv;
        }, {});
    };
    const groupedByLabel = groupByLabel(allLabelAndQuantity, 'label');

    const arrayOfColorsToDelete: string[] = [];
    // If sum of numbers in array is 0, return label
    for (const [key, value] of Object.entries(groupedByLabel as { [s: string]: string[] } | string[])) {
        const sum = value.reduce((a: number, b: number) => a + b, 0);
        if (sum <= 0) {
            arrayOfColorsToDelete.push(key);
        }
    }

    const arrayOfPossibleColors: string[] = [];
    for (const [key] of Object.entries(groupedByLabel as { [s: string]: string[] } | string[])) {
        arrayOfPossibleColors.push(key);
    }

    const checkIfArraysAreTheSame = (a: string[], b: string[]) => JSON.stringify(a) === JSON.stringify(b);
    const sameArrays = checkIfArraysAreTheSame(arrayOfPossibleColors, arrayOfColorsToDelete);

    // If arrays are of possible colors and colors to delete are the same it means that there is no quantity in sizes in any of the colors.
    // Return empty array. Keep colors visible.
    if (sameArrays) {
        return [];
    }

    return arrayOfColorsToDelete;
};

export function optionsAndVariants(
    item: productOptions_productOptions_items_ConfigurableProduct,
    upsell?: boolean,
): { options: ProductConfigOptions[]; variants: BaseVariant[] } {
    let colorsToDelete: string[] = [];
    if (upsell) {
        colorsToDelete = findVariantsWithNoQtyWithinAllSizes(item.variants);
    }

    const variants: BaseVariant[] = [];
    const options: ProductConfigOptions[] = cleanOptions(item.configurable_options as GqlOption[], colorsToDelete);
    (item.variants as GqlVariant[]).forEach((variant) => {
        const attr = (variant.attributes as GqlVariantAttr[])[0];
        if (attr && variant.product) {
            // if (colorsToDelete.includes(variant.attributes[0].label)) {
            //     return;
            // }
            variants.push({
                id: variant.product.id!,
                sku: variant.product.sku!,
                attributes: variant.attributes as ConfigurableAttributeOption[],
                size_id: variant.attributes?.find((a) => a!.code === 'size')?.value_index ?? 0,
                color_id: variant.attributes?.find((a) => a!.code === 'color')?.value_index ?? 0,
                stock_status:
                    variant.product.stock_status === 'OUT_OF_STOCK' ? StockStatus.OutOfStock : StockStatus.InStock,
                max_qty: variant.product.stock_qty || null,
                image_url: variant.product?.small_image?.url,
                colour_family: variant.product.colour_family,
                colour_family_label: variant.product.colour_family_label,
                hide_stock_source: variant.product.hide_stock_source,
            });
        }
    });
    return { options, variants };
}

/**
 * Clean up an individual option value
 * @param value
 */
export function optionValueFromQuery(value: GqlOptionValue): ProductConfigOptionsValues {
    return {
        label: value.label || 'unknown',
        default_label: value.default_label || 'unknown',
        store_label: value.store_label || 'unknown',
        use_default_value: value.use_default_value!,
        value_index: value.value_index!,
        swatch_data: getSwatchData(value),
    };
}

export function optionFromQuery(option: GqlOption, values?: ProductConfigOptionsValues[]): ProductConfigOptions {
    return {
        attribute_code: option.attribute_code || 'unknown',
        attribute_id: option.attribute_id || 'unknown',
        id: option.id!,
        label: option.label || 'unknown',
        position: option.position ?? 0,
        values: values || (option.values as GqlOptionValue[]).map(optionValueFromQuery),
    };
}

/**
 * @param options
 */
export function cleanOptions(options: GqlOption[], colorsToDelete: string[]): ProductConfigOptions[] {
    // This part filters colors showing only those which have quantity
    if (colorsToDelete.length > 0) {
        const [onlyOptionsWithQuantity] = options.map((option) => {
            return option.values?.filter((o) => {
                if (!colorsToDelete.includes(o?.label || '')) {
                    return option;
                }
            });
        });
        options[0].values = onlyOptionsWithQuantity || options[0].values;
        return options.map((option) => optionFromQuery(option));
    }
    return options.map((option) => optionFromQuery(option));
}

export function productVariantFromBase(base: BaseVariant, data: GqlProductListItem): ProductVariant {
    const stock_status = stockStatusFromQuery(data.stock_status, data.only_x_left_in_stock);
    const cleanedMedia = cleanMediaEntries(
        (data.media_gallery as productDetail_productDetail_media_gallery[]) || EMPTY_ARR,
    );
    return {
        ...base,
        media_entries: cleanedMedia,
        stock_status: stock_status,
        max_qty: data.stock_qty,
        small_image: imagePath(data.small_image?.url),
    };
}

function stockStatusFromQuery(stock_status: string | null, only_x_left_in_stock: number | null): StockStatus {
    if (stock_status === 'OUT_OF_STOCK') {
        return StockStatus.OutOfStock;
    }
    if (only_x_left_in_stock !== null) {
        return StockStatus.LowStock;
    }
    return StockStatus.InStock;
}

export type GqlProductListItem = productList_productList;
export type GqlOption = productOptions_productOptions_items_ConfigurableProduct_configurable_options;
export type GqlOptionValue = productOptions_productOptions_items_ConfigurableProduct_configurable_options_values;
export type GqlVariant = productOptions_productOptions_items_ConfigurableProduct_variants | any;
export type GqlVariantAttr = productOptions_productOptions_items_ConfigurableProduct_variants_attributes;
